import { Alert, Box, Button, Chip, Drawer, IconButton, Typography } from "@mui/material";
import {
  DEFAULT_ROLE_PERMISSIONS,
  GRANTABLE_PERMISSIONS,
  PERMISSION_SECTIONS,
  effectiveResellerPermissions,
  impliedBy,
  isEditableResellerRole,
  roleLabel,
  type ResellerMembership,
  type ResellerRoleMatrix,
} from "@xop/dashboard-shared";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import type { JSX } from "react";

type Mark = "role" | "granted" | "denied" | "off";

/**
 * Per-person overrides on top of the role (spec F §7.2).
 *
 * Every row is one grantable key with four possible states — from the role,
 * granted, denied, off — and the panel at the bottom is the EFFECTIVE set
 * computed with the same closure rule crm-api applies, so what the operator
 * sees here is what the server will enforce after Save. Keys the closure adds
 * are marked "implied by …", which is also why a deny can look like it did
 * nothing: the implier is still on.
 *
 * Owners never get this drawer — an owner is everything and cannot be
 * narrowed; the server refuses it anyway.
 *
 * Mirrors the reseller portal's own `PermissionsDrawer`
 * (`apps/web/src/pages/account/PermissionsDrawer.tsx` in xop-resellers) —
 * same behaviour, same `data-testid`s and `aria-label`s — adapted onto
 * crm-api's snake_case `ResellerMembership` (`permission_grants` /
 * `permission_denies` instead of the portal's camelCase `TeamMember`).
 */
export function ResellerPermissionsDrawer({
  member,
  matrix,
  matrixUnavailable,
  open,
  saving,
  error,
  onClose,
  onSave,
}: {
  member: ResellerMembership | null;
  matrix: ResellerRoleMatrix | undefined;
  /** True when the company's role matrix GET failed. The baseline shown then
   * falls back to `DEFAULT_ROLE_PERMISSIONS`, which may not be what this
   * company's row actually says — so Save is disabled rather than letting the
   * operator write grants/denies computed against a baseline that could be
   * stale or wrong. */
  matrixUnavailable?: boolean;
  open: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (next: { permission_grants: string[]; permission_denies: string[] }) => void;
}): JSX.Element {
  const [grants, setGrants] = useState<string[]>([]);
  const [denies, setDenies] = useState<string[]>([]);

  useEffect(() => {
    setGrants(member?.permission_grants ?? []);
    setDenies(member?.permission_denies ?? []);
  }, [member]);

  if (!member || member.role === "owner") return <Drawer open={false} onClose={onClose} />;

  const role = member.role;
  // The company's row for this role; failing that the shipped default for a
  // built-in, and NOTHING for a custom role (it has no default — and Save is
  // disabled below when the matrix failed, so an empty guess is never written).
  const bundle =
    matrix?.roles[role]?.permissions ?? (isEditableResellerRole(role) ? DEFAULT_ROLE_PERMISSIONS[role] : []);
  const effective = effectiveResellerPermissions({ role, bundle, grants, denies });

  const markOf = (key: string): Mark =>
    denies.includes(key) ? "denied" : grants.includes(key) ? "granted" : bundle.includes(key) ? "role" : "off";

  const grant = (key: string): void => {
    setDenies((d) => d.filter((k) => k !== key));
    setGrants((g) => (g.includes(key) ? g : [...g, key]));
  };
  const deny = (key: string): void => {
    setGrants((g) => g.filter((k) => k !== key));
    setDenies((d) => (d.includes(key) ? d : [...d, key]));
  };
  const clear = (key: string): void => {
    setGrants((g) => g.filter((k) => k !== key));
    setDenies((d) => d.filter((k) => k !== key));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 3 } }}
      aria-labelledby="permissions-drawer-title"
      role="dialog"
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography id="permissions-drawer-title" variant="h6">
          Permissions for {member.email}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </Box>
      <Typography sx={{ color: "text.secondary", mb: 2 }}>
        {roleLabel(member.role, matrix, member.role_label)} by role. Grant or deny individual permissions on top of it.
      </Typography>

      {matrixUnavailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The company&rsquo;s role matrix could not be loaded, so the baseline shown may be stale.
        </Alert>
      )}

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 1 }}>
        {GRANTABLE_PERMISSIONS.map((key) => {
          const mark = markOf(key);
          const held = effective.includes(key);
          const impliers = held && mark !== "granted" ? impliedBy(key, effective) : [];
          const [section, verb] = key.split(":");
          return (
            <Box
              key={key}
              data-testid={`perm-${key}`}
              data-mark={mark}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 14 }}>
                  {PERMISSION_SECTIONS[section as keyof typeof PERMISSION_SECTIONS]} · {verb}
                </Box>
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                  <code>{key}</code>
                  {mark === "role" && " · from role"}
                  {impliers.length > 0 && ` · implied by ${impliers.join(", ")}`}
                </Box>
              </Box>
              <Chip
                size="small"
                label={held ? "on" : "off"}
                color={held ? "success" : "default"}
                variant={held ? "filled" : "outlined"}
              />
              <Button
                size="small"
                variant={mark === "granted" ? "contained" : "text"}
                aria-label={`grant ${key}`}
                onClick={() => grant(key)}
              >
                Grant
              </Button>
              <Button
                size="small"
                color="error"
                variant={mark === "denied" ? "contained" : "text"}
                aria-label={`deny ${key}`}
                onClick={() => deny(key)}
              >
                Deny
              </Button>
              {(mark === "granted" || mark === "denied") && (
                <Button size="small" aria-label={`clear ${key}`} onClick={() => clear(key)}>
                  Clear
                </Button>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
          Effective
        </Typography>
        <Box data-testid="effective-permissions" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {effective.map((k) => (
            <Chip key={k} size="small" label={k} />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || Boolean(matrixUnavailable)}
          onClick={() => onSave({ permission_grants: grants, permission_denies: denies })}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
}
